// vg-binding: CONTEXT:decisions            (P15.D-14 render-at-read, P15.D-20 rewire-no-new-ui)
// vg-binding: INTERFACE-STANDARDS:error-shape
// apps/vendor/src/components/notifications/NotificationItem.tsx
//
// Single notification card consumed by the vendor NotificationCenter (bell).
// Pure presentational + click-handlers — list/query state lives upstream.
//
// Phase 15 Task 24: title/body arrive PRE-RENDERED at the caller locale from
// the canonical BE (P15.D-14) — this card displays them verbatim and no longer
// re-renders from `i18n_key`. A dedicated mark-read button (audience-scoped
// PATCH is wired in NotificationCenter) carries the `vendor-notif-markread-btn`
// test hook.

import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';

export interface NotificationItemData {
  id: string;
  title: string;
  body?: string;
  category: string; // server-side type/enum — displayed as a tag
  isRead: boolean;
  createdAt: string; // ISO datetime
  link?: string;
  /**
   * Retained for backward-compat with the presentational contract. Phase 15
   * renders title/body server-side (P15.D-14) so these are unused by the bell
   * (always null); kept optional so callers need not thread them.
   */
  i18nKey?: string | null;
  i18nParams?: Record<string, string | number> | null;
}

export interface NotificationItemProps {
  notification: NotificationItemData;
  /** Mark-as-read callback — wired to PATCH /api/v1/notifications/:id/read */
  onRead?: (id: string) => void;
  /** Optional click-through (e.g. SPA navigate to `notification.link`). */
  onActivate?: (notification: NotificationItemData) => void;
}

/**
 * Format ISO timestamp → relative time string using i18n keys.
 * Falls back to `toLocaleDateString(locale)` once the diff exceeds 1 week so
 * dates render in the user's active locale (vi/en/fr).
 */
function relativeTime(
  iso: string,
  t: (key: string, opts?: Record<string, unknown>) => string,
  locale: string,
): string {
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return '';
  const diffSec = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (diffSec < 60) return t('notifications.relativeTime.justNow');
  if (diffSec < 3600)
    return t('notifications.relativeTime.minutesAgo', {
      value: Math.floor(diffSec / 60),
    });
  if (diffSec < 86_400)
    return t('notifications.relativeTime.hoursAgo', {
      value: Math.floor(diffSec / 3600),
    });
  if (diffSec < 604_800)
    return t('notifications.relativeTime.daysAgo', {
      value: Math.floor(diffSec / 86_400),
    });
  const langTag = locale === 'vi' ? 'vi-VN' : locale === 'fr' ? 'fr-FR' : 'en-US';
  return new Date(ts).toLocaleDateString(langTag);
}

export function NotificationItem({
  notification,
  onRead,
  onActivate,
}: NotificationItemProps): JSX.Element {
  const { t, i18n } = useTranslation('orders');

  const handleActivate = (): void => {
    if (!notification.isRead && onRead) onRead(notification.id);
    if (onActivate) onActivate(notification);
  };

  const displayBody = notification.body;

  return (
    <div
      data-testid={`notification-item-${notification.id}`}
      data-unread={!notification.isRead}
      className="flex w-full items-start gap-3 border-b border-pw-border px-4 py-3 transition-colors hover:bg-pw-surface"
    >
      {/* Unread dot */}
      <span
        aria-hidden="true"
        className={[
          'mt-1 h-2 w-2 shrink-0 rounded-full',
          notification.isRead ? 'bg-transparent' : 'bg-pw-primary-strong',
        ].join(' ')}
      />
      <button
        type="button"
        onClick={handleActivate}
        className="min-w-0 flex-1 text-left focus:outline-none"
      >
        <div className="flex items-center justify-between gap-2">
          <p
            className={[
              'truncate text-sm',
              notification.isRead
                ? 'text-pw-text-muted'
                : 'font-medium text-pw-text',
            ].join(' ')}
          >
            {notification.title}
          </p>
          <span className="shrink-0 text-xs text-pw-text-muted">
            {relativeTime(notification.createdAt, t, i18n.language)}
          </span>
        </div>
        {displayBody ? (
          <p className="mt-1 line-clamp-2 text-xs text-pw-text-muted">
            {displayBody}
          </p>
        ) : null}
        <span className="mt-1 inline-block text-[10px] font-medium uppercase tracking-wide text-pw-text-muted">
          {notification.category}
        </span>
      </button>
      {!notification.isRead && onRead ? (
        <button
          type="button"
          data-testid="vendor-notif-markread-btn"
          onClick={(e) => {
            e.stopPropagation();
            onRead(notification.id);
          }}
          className="shrink-0 self-center rounded-md border border-pw-border bg-pw-bg px-2 py-1 text-xs font-medium text-pw-text-muted transition-colors hover:bg-pw-surface"
        >
          {t('notifications.page.markRead')}
        </button>
      ) : null}
    </div>
  );
}
